import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppSettingsService, LogService, HttpHelperService, AppStorageService } from '@oa/shared-core';
import { Attachment, AttachmentResult } from '../models';
import { HttpClient } from '@angular/common/http';
import { JsonPatchOperation } from '@oa/shared-core';

@Injectable()
export class AttachmentService {
  private readonly _serviceName = 'AttachmentService';

  constructor(
    private http: HttpClient,
    private httpHelper: HttpHelperService,
    readonly appSettings: AppSettingsService,
    private storage: AppStorageService
  ) {}

  getAll(listingId: number) {
    return this.http.get<Attachment[]>(listingUrl(listingId));
  }

  delete(listingId: number, attachmentId: number) {
    return this.http.delete(attachmentUrlFragment(listingId, attachmentId));
  }

  // tslint:disable-next-line:max-line-length
  update<Path extends keyof Attachment>({
    listingId,
    attachmentId,
    path,
    value,
  }: {
    path: Path;
    listingId: number;
    value: Attachment[Path];
    attachmentId: number;
  }): Observable<Object> {
    const url = attachmentUrlFragment(listingId, attachmentId);

    let jsonPatchDocument = [{ value, path: `/${path}`, op: 'replace' }] as JsonPatchOperation[];

    return this.httpHelper.patch(url, jsonPatchDocument);
  }

  rotate(listingId, attachmentId, rotation): Observable<Object> {
    return this.http.post(attachmentUrlFragment(listingId, attachmentId) + '/rotate', rotation);
  }

  sort(listingId, attachmentId, sortIndex): Observable<Object> {
    return this.http.post(attachmentUrlFragment(listingId, attachmentId) + '/sort', sortIndex);
  }

  /**
   * Gets the URL for an attachment image
   * @param param0
   */
  attachmentUrl({
    listingId,
    attachmentId,
    contentUpdatedOn,
    thumb = false,
  }: {
    listingId: number;
    contentUpdatedOn?;
    attachmentId: number;
    thumb?;
  }) {
    if (this.appSettings.azureAttachmentContainerEnabled && attachmentId > 0) {
      // the Azure Attachment Container store is the faster "cache" of the attachment data that is stored in the database.
      return this.attachmentUrlFromContainerStore({ listingId, attachmentId, contentUpdatedOn, thumb });
    } else {
      // the attachment data that is stored in the database, a little bit slower.
      return this.attachmentUrlFromPlatform({ listingId, attachmentId, contentUpdatedOn, thumb });
    }
  }

  /**
   *  Gets the URL for an attachment image from the OA Platform WebAPI
   * @param param0
   */
  attachmentUrlFromPlatform({
    listingId,
    attachmentId,
    contentUpdatedOn,
    thumb = false,
  }: {
    listingId: number;
    contentUpdatedOn?;
    attachmentId: number;
    thumb?;
  }) {
    let url = `${this.appSettings.apiBaseUrl}/listing/${listingId}/attachment/${attachmentId}${
      thumb ? '/thumb' : ''
    }`;
    if (contentUpdatedOn !== undefined) {
      url += '?=' + contentUpdatedOn;
    }
    return url;
  }

  /**
   * Gets the URL for an attachment image from the Azure Blob Container
   * @param param0
   */
  attachmentUrlFromContainerStore({
    listingId,
    attachmentId,
    contentUpdatedOn,
    thumb = false,
  }: {
    listingId: number;
    contentUpdatedOn?;
    attachmentId: number;
    thumb?;
  }) {
    // console.log(`${this._serviceName }.attachmentUrl()`);

    let baseUrl = this.appSettings.attachmentBaseUrl;

    let url = '';

    if (thumb) {
      url = `${baseUrl}/${listingId}/${attachmentId}-thumb.jpg`;
    } else {
      url = `${baseUrl}/${listingId}/${attachmentId}.jpg`;
    }

    if (contentUpdatedOn !== undefined) {
      url += '?=' + contentUpdatedOn;
    }

    return url;
  }

  public upload(listingId: number, files: File[]): Observable<AttachmentResult[]> {
    const url = this.appSettings.apiBaseUrl + listingUrl(listingId) + '/attachment';
    const form: FormData = new FormData();

    files.forEach((file) => {
      form.append('uploads[]', file, file.name);
    });

    return this.http.post<AttachmentResult[] | null>(url, form).pipe(map((response) => response ?? []));
  }
 
}

function listingUrl(listingId: number): string {
  return 'listing/' + listingId;
}

function attachmentUrlFragment(listingId: number, attachmentId: number) {
  return `${listingUrl(listingId)}/attachment/${attachmentId}`;
}
